// 말풍선 숨김 규칙의 안전 스위치 — 2026-09-27 말풍선 전수조사 최종 검수.
//
// 전역 CSS(src/theme/index.js)는 popper 가 트리거를 「가려짐」으로 판정해 붙이는 `data-popper-reference-hidden` 을 보고
// 팝오버·툴팁·메뉴를 숨긴다. 그런데 popper(@popperjs/core 2.11 dom-utils/getViewportRect.js)는 **사파리**에서
// 화면(visual viewport)의 위치 오프셋을 무시하고 크기만 쓴다(isLayoutViewport 가 false). 그래서 iOS 사파리에서
// **확대했거나 키보드가 올라와** 화면(visual viewport)이 문서 안에서 옮겨진 상태면, 눈에 보이는 트리거도 「가려짐」으로 판정돼
// 말풍선·메뉴가 사라진다. 그 상태에서만 숨김 규칙을 끈다(예전처럼 보이게 둔다) — 잘못 숨기는 쪽보다 낫다.
//
// 판정은 popper 와 같은 사용자 에이전트 규칙을 쓴다(아니면 둘이 어긋난다). html 에 `data-popper-hide` 가 있을 때만
// 숨김 규칙이 켜진다. 설치 전(서버 렌더 등)에는 속성이 없어 숨기지 않는다(fail-open).

const ATTR = "data-popper-hide";
const SAFARI_VISUAL_VIEWPORT = /^((?!chrome|android).)*safari/i; // popper isLayoutViewport 와 같은 식

/** popper utils/userAgent.js 와 같은 방식으로 UA 문자열을 만든다 */
export function uaStringOf(nav) {
  if (!nav) return "";
  const uaData = nav.userAgentData;
  if (uaData && Array.isArray(uaData.brands)) return uaData.brands.map((b) => `${b.brand}/${b.version}`).join(" ");
  return nav.userAgent || "";
}

/**
 * 지금 popper 의 「가려짐」 판정을 믿어도 되는가.
 * 사파리가 아니면 늘 믿는다(popper 가 오프셋을 반영한다). 사파리면 확대(scale)도 화면 이동(offset)도 없을 때만.
 * 크기는 비교하지 않는다 — 스크롤바·툴바로 창 크기와 몇 px 씩 늘 다르고(실측), 키보드로 높이만 줄어든 경우는
 * 키보드에 가려진 트리거를 숨기는 판정이 오히려 맞다. popper 가 틀리는 것은 오프셋을 0 으로 두는 것뿐이다.
 */
export function popperHideTrustworthy({ ua = "", vv = null } = {}) {
  if (!SAFARI_VISUAL_VIEWPORT.test(ua)) return true;
  if (!vv) return true; // visualViewport 가 없으면 popper 도 창 크기를 쓴다
  const near = (a, b, eps) => Math.abs(Number(a) - Number(b)) <= eps;
  return near(vv.scale ?? 1, 1, 0.01)
    && near(vv.offsetTop ?? 0, 0, 1)
    && near(vv.offsetLeft ?? 0, 0, 1);
}

let installed = false;

/** html 의 스위치 속성을 지금 상태로 맞추고, 화면 크기·확대·키보드가 바뀔 때마다 다시 맞춘다. 여러 번 불러도 한 번만 설치된다. */
export function installPopperHideGate(win = typeof window !== "undefined" ? window : undefined) {
  if (!win || !win.document || installed) return;
  // 레이아웃이 없는 테스트 환경(jsdom)에서는 켜지 않는다 — 모든 요소 크기가 0 이라 popper 가 트리거를 다 「가려짐」으로 표시하고,
  // 테마를 쓰는 테스트가 열린 말풍선을 toBeVisible 로 볼 때 숨겨져 실패한다(2026-09-27 재리뷰).
  if (/jsdom/i.test(uaStringOf(win.navigator))) return;
  installed = true;
  const html = win.document.documentElement;
  const update = () => {
    const ok = popperHideTrustworthy({ ua: uaStringOf(win.navigator), vv: win.visualViewport || null });
    if (ok) html.setAttribute(ATTR, ""); else html.removeAttribute(ATTR);
  };
  update();
  win.addEventListener("resize", update);
  if (win.visualViewport && typeof win.visualViewport.addEventListener === "function") {
    win.visualViewport.addEventListener("resize", update);
    win.visualViewport.addEventListener("scroll", update);
  }
}

/** 테스트 전용 — 설치 표시를 지운다 */
export function __resetPopperHideGateForTest() { installed = false; }
